import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useListings } from "../../hooks/useListings";

const SearchPage = () => {
  const {
    listings,
    loading: listingsLoading,
    error: listingsError,
    fetchListingsBySearch,
  } = useListings();
  const location = useLocation();
  const navigate = useNavigate();

  // Get search query from query string
  const params = new URLSearchParams(location.search);
  const query = params.get("query") || "";

  const [searchTerm, setSearchTerm] = useState(query);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [freeOnly, setFreeOnly] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch listings by search query, page, and perPage
  useEffect(() => {
    if (query) {
      fetchListingsBySearch(query, page, perPage).then((res) => {
        if (res && res.pagination) {
          setTotalPages(res.pagination.pages || 1);
        }
      });
      setSearchTerm(query);
    }
    // eslint-disable-next-line
  }, [query]);

  // Manual search trigger
  const handleSearch = () => {
    setPage(1);
    fetchListingsBySearch(searchTerm, 1, perPage).then((res) => {
      if (res && res.pagination) {
        setTotalPages(res.pagination.pages || 1);
      }
    });
  };

  // Filtering logic
  const filteredListings = useMemo(() => {
    let filtered = listings;
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (post) =>
          post.post_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (freeOnly) {
      filtered = filtered.filter((post) => {
        if (post.attributes && post.attributes.price !== undefined) {
          return Number(post.attributes.price) === 0;
        }
        return post.description?.toLowerCase().includes("free");
      });
    }
    if (priceMin !== "" || priceMax !== "") {
      filtered = filtered.filter((post) => {
        let price = null;
        if (post.attributes && post.attributes.price !== undefined) {
          price = Number(post.attributes.price);
        }
        if (price === null || isNaN(price)) return false;
        if (priceMin !== "" && price < Number(priceMin)) return false;
        if (priceMax !== "" && price > Number(priceMax)) return false;
        return true;
      });
    }
    return filtered;
  }, [listings, searchTerm, priceMin, priceMax, freeOnly]);

  if (listingsLoading) return <div className="p-8 text-center">Loading...</div>;
  if (listingsError)
    return (
      <div className="p-8 text-center text-red-600">
        Error loading search results.
      </div>
    );

  return (
    <div className="bg-[#f7f7f7] min-h-screen ">
      {/* Header */}
      <div className="flex items-center justify-between py-4 px-6 bg-white border-b border-gray-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline text-xs"
          >
            &lt; back
          </button>
        </div>
        <div className="text-center">
          {query && (
            <>
              <p className="text-xl md:text-2xl font-semibold text-gray-800">
                Search Results for {query}
              </p>
            </>
          )}
        </div>
        <div className="w-20"></div>
      </div>

      {/* Main content area  */}
      <div className="flex flex-row gap-6 max-w-7xl mx-auto pt-4 px-4 justify-center">
        {/* Listings grid with perPage input above */}
        <div className="flex-1">
          <div className="flex justify-end items-center mb-4 gap-2">
            <label className="text-xs text-gray-600">Per Page</label>
            <input
              type="number"
              className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
              min={1}
              max={100}
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value) || 1);
                setPage(1); // Reset to first page when perPage changes
              }}
            />
            <button
              className="ml-2 px-3 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700"
              onClick={handleSearch}
              type="button"
            >
              Search
            </button>
          </div>
          {filteredListings.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              No posts found for this search.
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {filteredListings.map((post) => (
                  <div
                    key={post.id}
                    className="bg-white rounded-lg shadow p-0 flex flex-col border border-gray-200 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => navigate(`/listings/${post.id}`)}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") navigate(`/listings/${post.id}`);
                    }}
                  >
                    {/* Image */}
                    {Array.isArray(post.images) && post.images.length > 0 ? (
                      <img
                        src={post.images[0]}
                        alt="post"
                        className="h-40 w-full object-cover"
                      />
                    ) : (
                      <div className="h-40 w-full bg-gradient-to-r from-blue-100 to-cyan-100 flex items-center justify-center text-xl text-gray-400 font-bold">
                        No Image
                      </div>
                    )}
                    {/* Card content */}
                    <div className="p-3 flex flex-col gap-1">
                      <h2 className="text-base font-semibold text-gray-800 mb-1 truncate">
                        {post.post_title}
                      </h2>
                      <div className="text-xs text-gray-700 mb-2 line-clamp-3">
                        {post.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Pagination controls */}
              <div className="flex justify-center mt-6 gap-2">
                <button
                  className="px-3 py-1 rounded border text-xs disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="px-2 py-1 text-xs">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="px-3 py-1 rounded border text-xs disabled:opacity-50"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
